import React, { useContext } from 'react';

import { media } from 'context';
import { Clickable, Icon, IconButton, Layout, Text } from 'ds';
import { formatUsd } from 'helpers';
import { selectCleaningServicePricing } from 'store/CleaningService/selectors';
import { useAppSelector } from 'store/hooks';

import CleaningDaySelector from './CleaningDaySelector';

interface CleaningServiceSelectDayCardProps {
  cleaningType: 'standard' | 'touch-up';
  touchUpSelected?: boolean;
  selectTouchUp?: () => void;
  menuPosition: 'top' | 'bottom';
  onSelect: ({ value }: { value: number }) => void;
  standardDayValue: number | null;
  touchUpDayValue: number | null;
  value: number | null;
  setModalIsVisible: (isVisible: boolean) => void;
}

const CleaningServiceSelectDayCard: React.FC<CleaningServiceSelectDayCardProps> = ({
  cleaningType,
  touchUpSelected,
  selectTouchUp,
  menuPosition,
  onSelect,
  standardDayValue,
  touchUpDayValue,
  value,
  setModalIsVisible
}) => {
  const { isMobile } = useContext(media);
  const pricing = useAppSelector(selectCleaningServicePricing);
  const icon = () => {
    if (cleaningType === 'standard') {
      return (
        <Layout color="blue-300" borderRadius={'circular'} width={24} height={24} justify="center" align="center">
          <Icon name="checkmark" size={'xs'} color="white" />
        </Layout>
      );
    } else if (cleaningType === 'touch-up' && touchUpSelected) {
      return (
        <Layout color="blue-500" borderRadius={'circular'} width={24} height={24} justify="center" align="center">
          <Icon name="checkmark" size={'xs'} color="white" />
        </Layout>
      );
    }
    return <Layout width={24} height={24} borderWidth={2} borderRadius={'circular'}></Layout>;
  };

  const dayIsDisabled = (day: number) => {
    if (cleaningType === 'standard' && touchUpDayValue === day) {
      return true;
    } else if (cleaningType === 'touch-up' && standardDayValue === day) {
      return true;
    }
    return false;
  };

  if (!pricing) {
    return null;
  }

  const topHalfContent = () => {
    return (
      <Layout
        direction="row"
        justify="space-between"
        align="center"
        paddingX={isMobile ? 20 : 24}
        paddingTop={24}
        paddingBottom={touchUpSelected || cleaningType === 'standard' ? 12 : 24}
      >
        <Layout direction="row" gap={isMobile ? 8 : 12} align="center">
          {icon()}
          <Layout direction="row" gap={8} align="center">
            <Text size={isMobile ? 'body-sm' : 'body-md'} semibold wrap={false}>
              {cleaningType === 'standard' ? 'Standard' : 'Touch-Up'}
            </Text>
            <IconButton
              name="info"
              size="xs"
              iconSize="xs"
              type="noBackground"
              onClick={e => {
                e.stopPropagation();
                setModalIsVisible(true);
              }}
            />
          </Layout>
          {cleaningType === 'touch-up' && (
            <Layout paddingX={4} height={16} align="center" color="gray-50" borderRadius={2}>
              <Text size="tag" semibold color="gray-700">
                OPTIONAL
              </Text>
            </Layout>
          )}
        </Layout>
        <Text size="body-md" semibold wrap={false}>
          {cleaningType === 'standard' ? formatUsd(pricing?.cleaning_price) : formatUsd(pricing?.touch_up_price)} ea.
        </Text>
      </Layout>
    );
  };

  return (
    <Layout width="100%" borderRadius={16} borderWidth={1} direction="column" gap={12}>
      <Layout direction="column">
        {cleaningType === 'standard' ? (
          topHalfContent()
        ) : (
          <Clickable
            onClick={e => {
              e.stopPropagation();
              selectTouchUp?.();
            }}
          >
            {topHalfContent()}
          </Clickable>
        )}
        {(cleaningType === 'standard' || touchUpSelected) && (
          <Layout width="100%" paddingX={isMobile ? 20 : 24} paddingBottom={24}>
            <CleaningDaySelector
              options={[
                { label: 'Monday', value: 1, disabled: dayIsDisabled(1) },
                { label: 'Tuesday', value: 2, disabled: dayIsDisabled(2) },
                { label: 'Wednesday', value: 3, disabled: dayIsDisabled(3) },
                { label: 'Thursday', value: 4, disabled: dayIsDisabled(4) },
                { label: 'Friday', value: 5, disabled: dayIsDisabled(5) }
              ]}
              value={value === null ? undefined : value}
              onSelect={onSelect}
              placeholder="Select day of week"
              menuPosition={menuPosition}
            />
          </Layout>
        )}
      </Layout>
    </Layout>
  );
};

export default CleaningServiceSelectDayCard;
